import { resolvePagesDataStoreKey } from '@/common/utils/resolvePageStoreEntry'
import { isMainFramePageData, isMainFrameSlot } from '@/common/utils/mainFrameSlot'
import { useDocumentTabsStore } from '@/stores/documentTabs'
import type { Router } from 'vue-router'

type DialogPageSlotAppStore = {
  dialogIds?: string[]
  dialogPageSlotIds?: Iterable<string>
  pagesData?: Record<string, unknown>
  currentPageId?: string | null
  updatePageId?: (id: string) => void
}

function slotMarkedAsDialog(appStore: DialogPageSlotAppStore, id: string): boolean {
  const known = appStore.dialogPageSlotIds
  if (known) {
    for (const slotId of known) {
      if (String(slotId) === id) return true
    }
  }
  return false
}

/** Dialog form slots (722, …) must not become document tabs or main-area routes. */
export function isDialogPageSlot(appStore: DialogPageSlotAppStore, slotId: unknown): boolean {
  const id = String(slotId ?? '').trim()
  if (!id) return false
  if (slotMarkedAsDialog(appStore, id)) return true
  if ((appStore.dialogIds ?? []).some((dialogId) => String(dialogId) === id)) {
    return true
  }
  const page = appStore.pagesData?.[id] as { $isDialog?: boolean } | undefined
  return page?.$isDialog === true
}

/** Block router.push / getPage for modal-only slots (incl. stale id after removeDialog). */
export function shouldSkipDocumentNavigationForSlot(
  appStore: DialogPageSlotAppStore,
  slotId: unknown,
): boolean {
  return isDialogPageSlot(appStore, slotId)
}

/** Active modal slots must survive Platform.vue page-data cleanup. */
export function shouldPreservePageDuringCleanup(
  appStore: DialogPageSlotAppStore,
  pageId: unknown,
  historyIds: ReadonlySet<string>,
): boolean {
  const id = String(pageId ?? '').trim()
  if (!id) return true
  if (isMainFrameSlot(id) && isMainFramePageData(appStore.pagesData?.[id])) return true
  if (isDialogPageSlot(appStore, id)) return true
  if (historyIds.has(id)) return true
  const pageData = appStore.pagesData?.[id] as { $parentPageId?: string } | undefined
  if (pageData?.$parentPageId && historyIds.has(String(pageData.$parentPageId))) {
    return true
  }
  return false
}

type DialogOpenEpochStore = {
  dialogIds?: string[]
  dialogSlotEpoch?: Record<string, number>
}

/** Drop in-flight NEW_DIALOG open when user closed the dialog before elementAction returned. */
export function isStaleDialogOpenAfterClose(
  appStore: DialogOpenEpochStore,
  formId: unknown,
  epochAtRequestStart: number,
): boolean {
  const id = String(formId ?? '').trim()
  if (!id) return false
  if ((appStore.dialogIds ?? []).some((dialogId) => String(dialogId) === id)) {
    return false
  }
  const currentEpoch = appStore.dialogSlotEpoch?.[id] ?? 0
  return currentEpoch !== epochAtRequestStart
}

/**
 * Нужно ли отбросить ответ NEW_DIALOG и не вызывать addDialog.
 *
 * Обёртка над {@link isStaleDialogOpenAfterClose}: защищает от «зомби-открытия», когда
 * пользователь закрыл диалог, пока elementAction ещё в полёте (epoch слота вырос).
 *
 * Важно про transfer:
 * - при transfer = id формы (например `2573`) слот известен заранее — сравниваем epoch;
 * - при transfer = `0` (document) id диалога приходит только в ответе (`form._attributes.id`),
 *   а epoch в store ключуется по этому id. Сравнение epoch по ключу `"0"` ломало повторное
 *   открытие: после закрытия epoch для `2573` становился 1, а на старте запроса читался 0
 *   для `"0"` → ответ ошибочно считался устаревшим и UI не показывался.
 *
 * @param intendedSlotId transfer из action (ожидаемый слот) или `0` для document
 * @param responseFormId id формы из ответа elementAction (ключ addDialog / dialogSlotEpoch)
 * @param epochAtRequestStart значение getDialogSlotEpoch на старте запроса (только для фиксированного transfer)
 * @returns true — не открывать диалог; false — можно вызывать addDialog
 */
export function shouldDropStaleDialogOpenAfterClose(
  appStore: DialogOpenEpochStore,
  intendedSlotId: unknown,
  responseFormId: unknown,
  epochAtRequestStart: number,
): boolean {
  const openedId = String(responseFormId ?? '').trim()
  if (!openedId) return false

  const intended = String(intendedSlotId ?? '').trim()
  if (!intended || intended === '0' || intended !== openedId) {
    return false
  }

  return isStaleDialogOpenAfterClose(appStore, openedId, epochAtRequestStart)
}

export function resolveDialogParentSlotAfterClose(
  appStore: DialogPageSlotAppStore,
  parentHint: string,
): string {
  const hint = String(parentHint ?? '').trim()
  const pages = (appStore.pagesData ?? {}) as Record<string, unknown>
  if (hint && pages[hint]) return hint
  const resolved = resolvePagesDataStoreKey(pages, {
    pageDataSlot: hint || undefined,
    currentPageId: appStore.currentPageId ?? undefined,
  })
  return resolved && pages[resolved] ? resolved : hint
}

/** Remove leaked document tab / route after dialog close. */
export async function finalizeDialogSlotCleanup(
  appStore: DialogPageSlotAppStore,
  closedDialogId: string,
  options?: {
    parentSlotHint?: string
    router?: Router
    routePageId?: string
  },
): Promise<string | null> {
  const closedId = String(closedDialogId ?? '').trim()
  if (!closedId) return null

  useDocumentTabsStore().removeTabEntry(closedId)

  const parentSlot = resolveDialogParentSlotAfterClose(
    appStore,
    String(options?.parentSlotHint ?? '').trim(),
  )
  if (!parentSlot) return null

  const cur = String(appStore.currentPageId ?? '')
  const routeId = String(options?.routePageId ?? '')
  const shouldRestoreFocus = cur === closedId || routeId === closedId
  if (!shouldRestoreFocus) return parentSlot

  if (appStore.pagesData?.[parentSlot]) {
    appStore.updatePageId?.(parentSlot)
  }

  if (options?.router && routeId === closedId) {
    const tabsStore = useDocumentTabsStore()
    const tab = tabsStore.tabs.find((t) => t.id === parentSlot)
    const routeName = tab?.routeName ?? 'document'
    await options.router.push({ name: routeName, params: { id: parentSlot } })
  }

  return parentSlot
}
